import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Icon from './Icon';
import { getAccessToken, isSecretaryContextUser } from '../authStorage';
import { fetchBranches } from '../branchesApi';
import { fetchBoardMembers } from '../boardApi';
import { createCommittee, fetchCommitteeById, mapCommitteeFormToPayload, updateCommittee } from '../committeesApi';
import { COMMITTEE_MEMBER_ROLES, COMMITTEE_TYPES, EMPTY_COMMITTEE_FORM, committeeMemberRoleLabel, committeeTypeLabel, validateCommitteeForm } from '../committeeFormUtils';
import { useLanguage } from '../i18n/language';
import { useT } from '../i18n/translations';
import './BranchesPage.css';
import './BoardPage.css';
import './DecisionFormPage.css';

export default function CommitteeFormPage({ mode }) {
  const { language, direction } = useLanguage();
  const t = useT();

  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/officer') ? '/officer' : '/app';
  const { id: idParam } = useParams();
  const isNew = mode === 'new';
  const editId = isNew ? null : Number(idParam);
  const invalidEditId = !isNew && (!Number.isFinite(editId) || editId < 1);
  const [branches, setBranches] = useState([]);
  const [boardMembers, setBoardMembers] = useState([]);
  const [form, setForm] = useState(EMPTY_COMMITTEE_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!getAccessToken()) { navigate('/login', { replace: true }); return; }
    if (!isSecretaryContextUser()) { setLoadError(t('common.noPerm')); setLoading(false); return; }
    if (invalidEditId) { setLoadError(t('committees.invalidId')); setLoading(false); return; }
    let cancelled = false;
    (async () => {
      setLoading(true); setLoadError('');
      try {
        const [companyRows, memberRows] = await Promise.all([fetchBranches(), fetchBoardMembers()]);
        if (cancelled) return;
        setBranches(companyRows);
        setBoardMembers(memberRows.filter((x) => x.status === 'active'));
        const firstBranchId = companyRows[0]?.id ? String(companyRows[0].id) : '';
        if (isNew) setForm({ ...EMPTY_COMMITTEE_FORM, branchId: firstBranchId, createdAt: new Date().toISOString().slice(0, 10) });
        else {
          const row = await fetchCommitteeById(editId);
          if (cancelled) return;
          setForm({
            name: row.name,
            type: row.type,
            description: row.description || '',
            createdAt: row.createdAt || '',
            branchId: row.branchId,
            isActive: !!row.isActive,
            members: (row.members || []).map((m) => ({ boardMemberId: String(m.boardMemberId), role: m.role || 'member' })),
          });
        }
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : t('committees.loadFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, isNew, editId, invalidEditId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    setErrors((p) => ({ ...p, [e.target.name]: '', _api: '' }));
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const availableMembers = boardMembers.filter((x) => String(x.branchId) === String(form.branchId));
  const memberNameById = useMemo(() => {
    const map = {};
    availableMembers.forEach((x) => { map[String(x.id)] = x.name; });
    return map;
  }, [availableMembers]);

  const addMember = () => {
    const first = availableMembers[0]?.id ? String(availableMembers[0].id) : '';
    setForm((p) => ({ ...p, members: [...(p.members || []), { boardMemberId: first, role: 'member' }] }));
  };

  const updateMember = (idx, key, value) => {
    setErrors((p) => ({ ...p, members: '', _api: '' }));
    setForm((p) => ({
      ...p,
      members: (p.members || []).map((m, i) => (i === idx ? { ...m, [key]: value } : m)),
    }));
  };

  const removeMember = (idx) => {
    setForm((p) => ({ ...p, members: (p.members || []).filter((_, i) => i !== idx) }));
  };

  const handleSave = async () => {
    const errs = validateCommitteeForm(form, {
      nameRequired: t('committees.valName'),
      branchRequired: t('committees.valBranch'),
      membersRequired: t('committees.valMembers'),
      membersInvalid: t('committees.valMembersInvalid'),
    });
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      const payload = mapCommitteeFormToPayload(form);
      if (isNew) await createCommittee(payload);
      else await updateCommittee(editId, payload);
      navigate(`${basePath}/committees`);
    } catch (e) {
      setErrors({ _api: e instanceof Error ? e.message : t('committees.saveFailed') });
    } finally {
      setSaving(false);
    }
  };

  const validateInfoStep = () => {
    const errs = {};
    if (!form.name?.trim()) errs.name = t('committees.valName');
    if (!form.branchId) errs.branchId = t('committees.valBranch');
    setErrors((p) => ({ ...p, ...errs }));
    return Object.keys(errs).length === 0;
  };

  const validateMembersStep = () => {
    const errs = validateCommitteeForm(form, {
      nameRequired: t('committees.valName'),
      branchRequired: t('committees.valBranch'),
      membersRequired: t('committees.valMembers'),
      membersInvalid: t('committees.valMembersInvalid'),
    });
    if (errs.members) {
      setErrors((p) => ({ ...p, members: errs.members }));
      return false;
    }
    return true;
  };

  const onNext = () => {
    if (step === 0 && !validateInfoStep()) return;
    if (step === 1 && !validateMembersStep()) return;
    setStep((s) => Math.min(2, s + 1));
  };

  return (
    <div className="decision-v2" dir={direction}>
      <div className="page-wrapper">
      {loadError && <div className="v2-error">{loadError}</div>}
      {loading && <div className="v2-loading">{t('committees.loading')}</div>}
      {!loading && !loadError && (
        <div className="card">
          <div className="card-top">
            <div className="top-bar top-bar--single-line">
              <div className="page-title-row">
                <h1 className="page-title">{isNew ? t('committees.titleNew') : t('committees.titleEdit')}<span className="page-title-icon"><Icon name="board" size={16} color="#0EA5A0" /></span></h1>
              </div>
              <div className="action-btns">
                <button type="button" className="btn btn-ghost" onClick={() => navigate(`${basePath}/committees`)}>{t('common.cancel')}</button>
                {step < 2 ? (
                  <button type="button" className="btn btn-primary" onClick={onNext}>{t('committees.nextBtn')}</button>
                ) : (
                  <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving || loading || !!loadError}>{saving ? t('committees.saving') : isNew ? t('committees.submitNew') : t('committees.submitEdit')}</button>
                )}
              </div>
            </div>
          </div>
          <div className="card-inner">
          <div style={{ display: 'flex', flexDirection: 'row', gap: 10, marginBottom: 18, alignItems: 'stretch' }}>
            {[t('committees.stepInfo'), t('committees.stepMembers'), t('committees.stepReview')].map((label, i) => {
              const done = i < step;
              const active = i === step;
              return (
                <div key={label} style={{ textAlign: 'center', flex: 1, color: active || done ? '#0EA5A0' : '#8AACAC', fontSize: '0.82rem' }}>
                  <button type="button" onClick={() => setStep(i)} style={{ width: '100%', border: `1.5px solid ${active || done ? '#0EA5A0' : '#DDE6E6'}`, background: active ? '#E0F7F6' : '#fff', padding: '10px 8px', fontWeight: 800, position: 'relative', cursor: 'pointer', color: 'inherit', fontFamily: 'Cairo, sans-serif' }}>
                    <span style={{ display: 'inline-block', minWidth: 20, marginInlineEnd: 6 }}>{done ? '✓' : String(i + 1)}</span>{label}
                    <span style={{ position: 'absolute', right: 0, left: 0, bottom: -1, height: 3, background: active ? '#0EA5A0' : 'transparent' }} />
                  </button>
                </div>
              );
            })}
          </div>
          <div className="mform">
            {errors._api && <div style={{ marginBottom: 12, padding: '10px 12px', background: '#ffebee', color: '#c62828', borderRadius: 8 }}>{errors._api}</div>}
            {step === 0 && <div className="mform__row mform__row--4">
              <div className="mform__field"><label className="mform__label">{t('committees.lblName')} <span>*</span></label><div className="mform__input-wrap"><Icon name="board" size={14} color="#02c3a6" className="mform__input-icon" /><input className={`mform__input mform__input--with-icon${errors.name ? ' mform__input--error' : ''}`} name="name" value={form.name} onChange={handleChange} /></div>{errors.name && <span className="mform__error">{errors.name}</span>}</div>
              <div className="mform__field"><label className="mform__label">{t('committees.lblType')}</label><div className="mform__input-wrap"><Icon name="filter" size={14} color="#8b5cf6" className="mform__input-icon" /><select className="mform__select mform__input--with-icon" name="type" value={form.type} onChange={handleChange}>{COMMITTEE_TYPES.map((x) => <option key={x.value} value={x.value}>{committeeTypeLabel(x.value, language)}</option>)}</select></div></div>
              <div className="mform__field"><label className="mform__label">{t('committees.lblBranch')} <span>*</span></label><div className="mform__input-wrap"><Icon name="branch" size={14} color="#1b8eff" className="mform__input-icon" /><select className={`mform__select mform__input--with-icon${errors.branchId ? ' mform__select--error' : ''}`} name="branchId" value={form.branchId} onChange={handleChange}><option value="">{t('committees.pickBranch')}</option>{branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}</select></div>{errors.branchId && <span className="mform__error">{errors.branchId}</span>}</div>
              <div className="mform__field"><label className="mform__label">{t('committees.lblCreatedAt')}</label><div className="mform__input-wrap"><Icon name="calendar" size={14} color="#0ea5a4" className="mform__input-icon" /><input className="mform__input mform__input--with-icon" name="createdAt" type="date" value={form.createdAt} onChange={handleChange} /></div></div>
            </div>}
            {step === 0 && <div className="mform__row mform__row--4">
              <div className="mform__field mform__field--span3"><label className="mform__label">{t('committees.lblDescription')}</label><div className="mform__input-wrap"><Icon name="report" size={14} color="#8b5cf6" className="mform__input-icon" /><input className="mform__input mform__input--with-icon" name="description" value={form.description} onChange={handleChange} /></div></div>
              <div className="mform__field"><label className="mform__label">{t('committees.lblStatus')}</label><div className="mform__input-wrap"><Icon name="settings" size={14} color="#14b8a6" className="mform__input-icon" /><select className="mform__select mform__input--with-icon" name="isActive" value={String(form.isActive)} onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.value === 'true' }))}><option value="true">{t('committees.statusActive')}</option><option value="false">{t('committees.statusInactive')}</option></select></div></div>
            </div>}

            {step === 1 && <div className="mform__section-title"><Icon name="user" size={13} color="#02c3a6" /> {t('committees.lblMembers')}</div>}
            {step === 1 && errors.members && <div className="mform__error" style={{ marginBottom: 8 }}>{errors.members}</div>}
            {step === 1 && (form.members || []).map((m, idx) => (
              <div className="mform__row mform__row--4" key={`${idx}-${m.boardMemberId}-${m.role}`}>
                <div className="mform__field mform__field--span2">
                  <label className="mform__label">{t('committees.pickMember')}</label>
                  <div className="mform__input-wrap">
                    <Icon name="user" size={14} color="#02c3a6" className="mform__input-icon" />
                    <select className="mform__select mform__input--with-icon" value={m.boardMemberId} onChange={(e) => updateMember(idx, 'boardMemberId', e.target.value)}>
                      <option value="">{t('committees.pickMember')}</option>
                      {availableMembers.map((bm) => <option key={bm.id} value={bm.id}>{bm.name}</option>)}
                    </select>
                  </div>
                </div>
                <div className="mform__field">
                  <label className="mform__label">{t('committees.role')}</label>
                  <div className="mform__input-wrap">
                    <Icon name="lock" size={14} color="#1b8eff" className="mform__input-icon" />
                    <select className="mform__select mform__input--with-icon" value={m.role} onChange={(e) => updateMember(idx, 'role', e.target.value)}>
                      {COMMITTEE_MEMBER_ROLES.map((r) => <option key={r.value} value={r.value}>{committeeMemberRoleLabel(r.value, language)}</option>)}
                    </select>
                  </div>
                </div>
                <div className="mform__field mform__field--span2" style={{ display: 'flex', alignItems: 'end', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ color: '#607d8b', fontSize: '0.82rem' }}>{memberNameById[m.boardMemberId] ? `${memberNameById[m.boardMemberId]} — ${committeeMemberRoleLabel(m.role, language)}` : '—'}</span>
                  <button type="button" className="btn btn--ghost" onClick={() => removeMember(idx)}><Icon name="trash" size={13} /> {t('committees.removeMember')}</button>
                </div>
              </div>
            ))}
            {step === 1 && <button type="button" className="btn btn--outline" onClick={addMember}><Icon name="plus" size={13} color="#02c3a6" /> {t('committees.addMember')}</button>}

            {step === 2 && (
              <div style={{ border: '1px solid #e4ecf7', padding: 14 }}>
                <div style={{ fontWeight: 800, color: '#00334a', marginBottom: 8 }}>{form.name || '—'}</div>
                <div style={{ color: '#607d8b', marginBottom: 4 }}>{t('committees.lblType')}: {committeeTypeLabel(form.type, language)}</div>
                <div style={{ color: '#607d8b', marginBottom: 4 }}>{t('committees.lblBranch')}: {branches.find((b) => String(b.id) === String(form.branchId))?.name || '—'}</div>
                <div style={{ color: '#607d8b', marginBottom: 4 }}>{t('committees.lblMembers')}: {(form.members || []).length}</div>
                <div style={{ color: '#607d8b' }}>{t('committees.lblDescription')}: {form.description || '—'}</div>
              </div>
            )}
          </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
